import { useEffect, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { uploader } from '../upload/uploader';
import { uploadAccept } from '../lib/upload-accept';

/** Window-wide drag-and-drop target for users allowed to upload. */
export function DropZone({ enabled }: { enabled: boolean }) {
  const [over, setOver] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => { if (hasFiles(e)) { depth++; setOver(true); } };
    const leave = () => { depth = Math.max(0, depth - 1); if (!depth) setOver(false); };
    const overFn = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setOver(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) uploader.add(files, { placeholder: uploader.defaultTarget ?? undefined });
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', overFn);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', overFn);
      window.removeEventListener('drop', drop);
    };
  }, [enabled]);
  if (!over) return null;
  return (
    <div className="dropzone-overlay">
      <div className="target">
        <UploadCloud size={36} color="var(--accent-strong)" />
        <strong style={{ fontSize: 17 }}>Drop to upload</strong>
        <span className="muted">Video and audio files go to the workspace’s default storage, then PRISM inspects them and builds proxies.</span>
      </div>
    </div>
  );
}

/**
 * The picker input is kept in the document and in a module variable while the
 * native dialog is open: a detached input nobody references can be garbage
 * collected (WebKit, sometimes Chromium) and then never fires `change`.
 */
let pending: HTMLInputElement | null = null;

function closePicker(input: HTMLInputElement) {
  input.value = '';
  input.remove();
  if (pending === input) pending = null;
}

export function pickFiles() {
  if (pending) closePicker(pending);
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = uploadAccept();
  input.style.display = 'none';
  input.addEventListener('change', () => {
    const files = Array.from(input.files ?? []);
    closePicker(input);
    if (files.length) uploader.add(files);
  });
  input.addEventListener('cancel', () => closePicker(input));
  pending = input;
  document.body.appendChild(input);
  input.click();
}
